import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import SecondaryCheckbox from "Components/common/SecondaryCheckbox";
import { sequenceStoreData } from "Stores/sequenceData";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import userDetail from "Stores/userDetail";
import { SequenceUtils } from "Utils/SequenceUtils";
import SearchInputBox from "Components/common/SearchInputBox";
import LiAccountListItem from "./LiAccountListItem";
import SelectedLiAccountList from "./SelectedLiAccountList";

// Lets the status rail open this card's Add-account picker in place, same as
// the email inbox card's requestOpenAddInbox.
let openAddLiAccountExternally = () => {};
const requestOpenAddLiAccount = () => openAddLiAccountExternally();

const liAccountLabel = account => {
  const { firstName = "", lastName = "", email = "", publicId = "" } =
    account || {};
  const name = `${firstName} ${lastName}`.trim();
  return name || email || publicId || "LinkedIn account";
};

const MultiLiAccountSelection = observer(props => {
  const history = useHistory();
  const {
    title = "Sending LinkedIn accounts",
    sequence = {},
    sequenceId = "",
    isSharedSequence = false,
    isLiAccountMappedToSequence = false,
    getSelectedSeqData = () => {},
    hasSubLabel = true,
    showHeader = true,
    className = "",
    cbk = () => {},
    showLimit = false
  } = props || {};

  const { selectedLiAccounts = [], setSelectedLiAccounts = () => {} } =
    sequenceStoreData;

  const liRequired =
    SequenceUtils.checkLinkedinAutoSteps(sequence) && !selectedLiAccounts?.length;

  const [showDropdown, setShowDropdown] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [tempSelection, setTempSelection] = useState([]);
  const [isAddingAccounts, setIsAddingAccounts] = useState(false);

  // No LinkedIn account on the whole workspace: the Add button becomes a
  // one-click redirect to Settings instead of opening an empty picker.
  const hasWorkspaceLiAccounts = (userDetail?.activeLinkedInAccounts || [])
    .length > 0;

  const redirectToConnectLinkedIn = () => {
    cbk();
    history.push("/settings/account/linkedin");
  };

  const addAccountAnchorRef = useRef(null);

  useEffect(() => {
    openAddLiAccountExternally = () => {
      if (addAccountAnchorRef?.current) {
        addAccountAnchorRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
      if (hasWorkspaceLiAccounts) {
        setShowDropdown(true);
      } else {
        redirectToConnectLinkedIn();
      }
    };
    return () => {
      openAddLiAccountExternally = () => {};
    };
  });

  const removeLiAccountFromSeq = async (id = "", cbk = () => {}) => {
    const confirmationCbk = async () => {
      await sequenceStoreData.removeLiAccountFromSequence(
        {
          liAccountId: id,
          sequenceId
        },
        cbk
      );
    };

    const isLastAccount = selectedLiAccounts?.length <= 1;

    confirmationPopupState.setPopupValues({
      content: isLastAccount
        ? `This is the only LinkedIn account connected to this sequence. Removing it will pause the sequence, since there won't be an account left to send LinkedIn steps from.`
        : `Are you sure you want to remove this LinkedIn account from the sequence?
        Contacts already assigned to it will move to another connected account.`,
      actionBtnText: "Proceed",
      callback: () => confirmationCbk(),
      loadingMsg: "Please wait..."
    });
    confirmationPopupState.setShowPopup(true);
  };

  const handleSearch = value => {
    setSearchText(value);
  };

  const handleSelectAll = checked => {
    if (checked) {
      const filtered = accounts.filter(item =>
        item?.label.toLowerCase().includes(searchText.toLowerCase())
      );
      const newSelection = filtered.map(item => ({
        id: item.id,
        label: item.label,
        isPremium: item.isPremium
      }));
      const uniqueSelection = [...tempSelection];
      newSelection.forEach(item => {
        if (!uniqueSelection.some(ele => ele.id === item.id)) {
          uniqueSelection.push(item);
        }
      });
      setTempSelection(uniqueSelection);
    } else {
      const filtered = accounts.filter(item =>
        item?.label.toLowerCase().includes(searchText.toLowerCase())
      );
      const remainingSelection = tempSelection.filter(
        item => !filtered.some(ele => ele.id === item.id)
      );
      setTempSelection(remainingSelection);
    }
  };

  const handleChange = (checked, id, label) => {
    if (checked) {
      const account = accounts.find(item => item.id === id);
      setTempSelection([
        ...tempSelection,
        { id, label, isPremium: account?.isPremium }
      ]);
    } else {
      setTempSelection(tempSelection.filter(item => item.id !== id));
    }
  };

  const handleBatchAddAccounts = async () => {
    if (tempSelection.length === 0) return;

    setIsAddingAccounts(true);
    const liAccountIds = tempSelection.map(item => item.id);

    const cbk = success => {
      if (success) {
        const newSelected = [...selectedLiAccounts];
        tempSelection.forEach(item => {
          if (!newSelected.some(ele => ele.id === item.id)) {
            newSelected.push(item);
          }
        });
        setSelectedLiAccounts(newSelected);
        getSelectedSeqData();
        setShowDropdown(false);
        setTempSelection([]);
      }
      setIsAddingAccounts(false);
    };

    await sequenceStoreData.addLiAccountsToSequence(
      { sequenceId, liAccountIds },
      cbk
    );
  };

  const removeAccount = async (id = "") => {
    const cbk = () => {
      let temp = [...selectedLiAccounts];
      temp = temp.filter(item => item?.id !== id);
      setSelectedLiAccounts([...temp]);
      confirmationPopupState.setShowPopup(false);
    };
    removeLiAccountFromSeq(id, cbk);
  };

  const constructDropDownData = () => {
    const list = (userDetail?.activeLinkedInAccounts || []).map(item => ({
      id: item?.id,
      label: liAccountLabel(item),
      isPremium: item?.linkedinAccountType
        ? item.linkedinAccountType?.toLowerCase() !== "classic"
        : false
    }));
    setAccounts(list);
  };

  useEffect(() => {
    constructDropDownData();
    return () => {
      setAccounts([]);
    };
  }, [isSharedSequence, userDetail?.activeLinkedInAccounts]);

  return (
    <div className={`sequenceMultiInboxSelection ${className}`}>
      {showHeader && (
        <div className="inboxSectionHeader">
          <div className="inboxSectionTitle">
            <h6>{title}</h6>
            {liRequired ? (
              <span className="requiredChip">Required</span>
            ) : (
              selectedLiAccounts?.length > 0 && (
                <span className="inboxCountBadge">
                  {selectedLiAccounts.length} connected
                </span>
              )
            )}
          </div>
        </div>
      )}
      {hasSubLabel && (
        <p className="subTextForSettings">
          LinkedIn steps send from these accounts. Contacts are split evenly
          across them.{" "}
          <button className="hereText" onClick={redirectToConnectLinkedIn}>
            Learn more.
          </button>
        </p>
      )}
      {isLiAccountMappedToSequence && !selectedLiAccounts?.length && (
        <p className="inboxEmptyStateLine">
          {liRequired
            ? sequence?.status?.toLowerCase() === "active"
              ? "This sequence has LinkedIn steps but no account connected. LinkedIn steps cannot send."
              : "This sequence has LinkedIn steps. Connect at least one account to activate it."
            : "No LinkedIn accounts connected yet."}
        </p>
      )}
      <div className="addInboxAnchor" ref={addAccountAnchorRef}>
        <button
          type="button"
          className="addInboxBtn"
          onClick={() =>
            hasWorkspaceLiAccounts
              ? setShowDropdown(true)
              : redirectToConnectLinkedIn()
          }
        >
          <i className="material-icons">add</i>
          <span className="btnText">
            {hasWorkspaceLiAccounts ? "Add LinkedIn account" : "Connect LinkedIn account"}
          </span>
        </button>
        {showDropdown && (
          <>
            <div className="dropdownInboxList">
              <div className="dropdownOptions">
                {!accounts.length ? (
                  <div className="noInboxConnectedState">
                    <div className="noInboxText">
                      You haven't connected a LinkedIn account yet. Connect
                      one to start sending LinkedIn steps.
                    </div>
                    <button
                      type="button"
                      className="connectInboxCtaBtn"
                      onClick={redirectToConnectLinkedIn}
                    >
                      Connect LinkedIn account
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="searchWrapper">
                      <SearchInputBox
                        placeholder="Search by name"
                        valueCbk={handleSearch}
                        hasCloseIcon={true}
                        hasSearchIcon={false}
                      />
                    </div>
                    <div className="selectAllWrapper">
                      {(() => {
                        const filtered =
                          accounts?.filter(item =>
                            item?.label
                              .toLowerCase()
                              .includes(searchText.toLowerCase())
                          ) || [];
                        const isAllSelected =
                          filtered.length > 0 &&
                          filtered.every(
                            item =>
                              selectedLiAccounts.some(
                                ele => ele.id === item.id
                              ) ||
                              tempSelection.some(ele => ele.id === item.id)
                          );
                        const isSelectAllDisabled =
                          filtered.length === 0 ||
                          filtered.every(item =>
                            selectedLiAccounts.some(ele => ele.id === item.id)
                          );
                        return (
                          <SecondaryCheckbox
                            label="Select All"
                            id="selectAllLiAccounts"
                            cbk={handleSelectAll}
                            checked={isAllSelected}
                            disabled={isSelectAllDisabled}
                          />
                        );
                      })()}
                    </div>
                    <div className="inboxList">
                      {(() => {
                        const filtered =
                          accounts?.filter(item =>
                            item?.label
                              .toLowerCase()
                              .includes(searchText.toLowerCase())
                          ) || [];
                        return filtered.length > 0 ? (
                          filtered.map(item => {
                            const { id = "", label = "", isPremium = false } =
                              item || {};
                            return (
                              <LiAccountListItem
                                key={id}
                                id={id}
                                label={label}
                                isPremium={isPremium}
                                cbk={handleChange}
                                checked={tempSelection.some(
                                  ele => ele.id === id
                                )}
                                disabled={selectedLiAccounts?.some(
                                  ele => ele.id === id
                                )}
                              />
                            );
                          })
                        ) : (
                          <div className="inboxEmptyState">
                            <span>No LinkedIn accounts found</span>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="dropdownFooter">
                      <button
                        className={`addMultipleInboxesBtn ${
                          tempSelection.length === 0 ? "disabled" : ""
                        }`}
                        onClick={handleBatchAddAccounts}
                        disabled={tempSelection.length === 0 || isAddingAccounts}
                      >
                        {isAddingAccounts
                          ? "Adding..."
                          : tempSelection.length === 0
                          ? "Select accounts to add"
                          : `Add ${tempSelection.length} Account${
                              tempSelection.length > 1 ? "s" : ""
                            }`}
                      </button>
                      <div
                        className="connectInboxFullRow"
                        onClick={redirectToConnectLinkedIn}
                      >
                        <i className="material-icons">add</i>
                        <span className="btnText">
                          Connect new LinkedIn account
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div
              className="multiInboxDropdownOverlay"
              onClick={() => {
                setShowDropdown(false);
                setTempSelection([]);
                setSearchText("");
              }}
            />
          </>
        )}
      </div>
      {selectedLiAccounts?.length > 0 && (
        <SelectedLiAccountList
          accounts={selectedLiAccounts}
          loading={!isLiAccountMappedToSequence}
          canRemove={true}
          onRemove={removeAccount}
          showLimit={showLimit}
        />
      )}
    </div>
  );
});

export default MultiLiAccountSelection;
export { MultiLiAccountSelection, requestOpenAddLiAccount };
